import React, { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import Userfront from "../auth/userfrontCompat";
import { useTranslation } from "react-i18next";
import { Modal, Button } from "react-bootstrap";
import countriesLocation from "../components/CountriesLocation";
import { useNavigate } from "react-router-dom";
import { usePersistentCollapsedState } from "../components/SidebarContext";

const StepPill = ({ index, active, done, label, onClick }) => {
  const base =
    "flex items-center gap-2 px-3 py-2 rounded-full border text-sm transition whitespace-nowrap cursor-pointer";
  const stateClass = done
    ? "bg-green-50 border-green-200 text-green-700"
    : active
    ? "bg-blue-50 border-blue-200 text-blue-700"
    : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50";

  return (
    <div className={`${base} ${stateClass}`} role="button" tabIndex={0} onClick={onClick}>
      <div
        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold
          ${done ? "bg-green-600 text-white" : active ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-600"}`}
      >
        {done ? "✓" : index}
      </div>
      <span className="font-medium">{label}</span>
    </div>
  );
};

const InfoPanel = ({ title, bullets, imageSrc }) => {
  return (
    <div className="relative w-full h-full rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-sm">
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${imageSrc})` }}
        />
        <div className="absolute inset-0 bg-black/45" />
      </div>

      <div className="relative p-6 sm:p-8 text-white min-h-[320px] flex flex-col justify-between">
        <div>
          <div className="inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full bg-white/10 border border-white/20">
            Leadgenflow onboarding
          </div>

          <h3 className="text-2xl sm:text-3xl font-semibold mt-4 leading-tight">{title}</h3>

          {bullets?.length > 0 && (
            <ul className="mt-4 space-y-2 text-sm text-white/90">
              {bullets.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span className="mt-0.5">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="text-xs text-white/70">
          Tip: keep your country aligned with your proxy location for the most stable LinkedIn sessions.
        </div>
      </div>
    </div>
  );
};

const Onboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const collapsed = usePersistentCollapsedState();

  const loggedInEmail = Userfront.user.email;

  // 1=Location, 2=LinkedIn, 3=Email (optional), 4=Done
  const [currentStep, setCurrentStep] = useState(1);

  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  // Step 1
  const [country, setCountry] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);

  // Step 2
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [selectedMode, setSelectedMode] = useState("linkedin_no_2fa");
  const [savingCookie, setSavingCookie] = useState(false);

  // Status flags
  const [linkedinConnected, setLinkedinConnected] = useState(false);
  const [linkedinStatusLoading, setLinkedinStatusLoading] = useState(false);

  // Step 3
  const [oauthStatus, setOauthStatus] = useState(null);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthStarting, setOauthStarting] = useState(false);
  const [skippedEmail, setSkippedEmail] = useState(false);

  // Confirmation
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // 2FA
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [pin, setPin] = useState("");
  const [sessionId, setSessionId] = useState(null);

  // generic
  const [error, setError] = useState("");

  const sortedCountries = useMemo(
    () => [...countriesLocation].sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  // ✅ Translation fallback helper:
  // if i18next returns the key itself (missing translation), show a human fallback.
  const tt = (key, fallback) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  const safeText = (v) => (v == null ? "" : String(v));
  const safeErrorText = (e) => {
    const msg = e?.response?.data?.error ?? e?.response?.data ?? e?.message ?? e;
    if (typeof msg === "string") return msg;
    try {
      return JSON.stringify(msg);
    } catch {
      return "Unknown error";
    }
  };

  // ---------------------------------------
  // Best-effort LinkedIn “connected” detect
  // ---------------------------------------
  const inferLinkedInConnectedFromUser = (u) => {
    if (!u) return false;

    const directFlags = [
      u.linkedinConnected,
      u.linkedInConnected,
      u.hasLinkedInCookie,
      u.hasLinkedinCookie,
      u.linkedinCookieConnected,
      u.linkedinCookieSaved,
    ].some((x) => x === true);

    if (directFlags) return true;

    const cookieCandidates = [
      u.li_at,
      u.liAt,
      u.liAtCookie,
      u.linkedinCookie,
      u.linkedInCookie,
      u.cookie,
      u.cookies,
      u.linkedinCookies,
      u.linkedInCookies,
    ];

    for (const c of cookieCandidates) {
      if (typeof c === "string" && c.trim().length > 10) return true;
      if (c && typeof c === "object" && Object.keys(c).length > 0) return true;
      if (Array.isArray(c) && c.length > 0) return true;
    }

    const timeCandidates = [
      u.linkedinCookieSetAt,
      u.linkedInCookieSetAt,
      u.linkedinConnectedAt,
      u.linkedInConnectedAt,
      u.linkedinCookieUpdatedAt,
    ];
    for (const d of timeCandidates) {
      if (d) return true;
    }

    return false;
  };

  const fetchLinkedInStatusSafe = async (uMaybe) => {
    setLinkedinStatusLoading(true);
    try {
      try {
        const r = await api.get("/linkedin/status");
        const connected =
          r?.data?.connected === true ||
          r?.data?.status?.connected === true ||
          r?.data?.hasCookie === true ||
          r?.data?.cookieSaved === true;

        if (connected) {
          setLinkedinConnected(true);
          return true;
        }
        if (r?.data?.connected === false || r?.data?.status?.connected === false) {
          setLinkedinConnected(false);
          return false;
        }
      } catch (_) {}

      const u = uMaybe || currentUser || (await api.get("/user/user")).data.user;
      const connected = inferLinkedInConnectedFromUser(u);
      setLinkedinConnected(connected);
      return connected;
    } catch (_) {
      setLinkedinConnected(false);
      return false;
    } finally {
      setLinkedinStatusLoading(false);
    }
  };

  const fetchOAuthStatusSafe = async () => {
    setOauthLoading(true);
    try {
      const res = await api.get("/email/oauth/status");
      const st = res.data?.status || null;
      setOauthStatus(st);
      return !!st?.connected;
    } catch (_) {
      setOauthStatus(null);
      return false;
    } finally {
      setOauthLoading(false);
    }
  };

  // ---------------------------------------
  // Bootstrap: choose correct starting step
  // ---------------------------------------
  useEffect(() => {
    const bootstrap = async () => {
      setLoadingUser(true);
      setError("");

      try {
        const userResponse = await api.get("/user/user");
        const u = userResponse.data.user;
        setCurrentUser(u);

        const savedCountry = u?.country || "";
        setCountry(savedCountry);

        // Determine LinkedIn + Email statuses *deterministically*
        const liConnected = await fetchLinkedInStatusSafe(u);
        const emailConnected = await fetchOAuthStatusSafe();
        const persistedEmailSkip = !!u?.onboardingEmailSkipped;
        setSkippedEmail(persistedEmailSkip);

        // Email is optional, but skipping it must survive a refresh.
        if (savedCountry && liConnected && (emailConnected || persistedEmailSkip)) {
          setCurrentStep(4);
          return;
        }

        if (!savedCountry) {
          setCurrentStep(1);
          return;
        }

        if (!liConnected) {
          setCurrentStep(2);
          return;
        }

        // LinkedIn ok, email not connected: go to optional email step
        setCurrentStep(3);
      } catch (e) {
        console.error("Failed to bootstrap onboarding:", e);
        setCurrentStep(1);
      } finally {
        setLoadingUser(false);
      }
    };

    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh when arriving at steps
  useEffect(() => {
    if (currentStep === 2) fetchLinkedInStatusSafe();
    if (currentStep === 3) fetchOAuthStatusSafe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const savedCountryExists = !!currentUser?.country;

  // Step completion
  const step1Done = !!currentUser?.country;
  const step2Done = !!linkedinConnected;
  const step3Done = !!oauthStatus?.connected || skippedEmail;
  const step4Done = currentStep === 4;

  // -----------------------------
  // Step 1: Save
  // -----------------------------
  const saveLocation = async () => {
    if (!country || savingLocation) return;

    if (currentUser?.country && String(currentUser.country) === String(country)) {
      setCurrentStep(2);
      return;
    }

    setSavingLocation(true);
    setError("");

    try {
      await api.put("/user/location", { country });

      const u = (await api.get("/user/user")).data.user;
      setCurrentUser(u);

      setCurrentStep(2);
    } catch (e) {
      console.error("Saving location failed:", e);
      setError(tt("errors.saveSettings", "Failed to save settings."));
    } finally {
      setSavingLocation(false);
    }
  };

  // -----------------------------
  // Step 2: LinkedIn connect
  // -----------------------------
  const beginLinkedInLogin = async () => {
    if (savingCookie) return;
    setSavingCookie(true);
    setError("");

    try {
      const u = currentUser || (await api.get("/user/user")).data.user;

      const { data } = await api.post("/linkedin/start-login", {
        userEmail: u.email,
        email: emailInput,
        password: passwordInput,
        proxyServer: u.proxyServer || null,
        mode: selectedMode,
      });

      if (data?.success && data.twoFARequired) {
        setSessionId(data.sessionId);
        setShow2FAModal(true);
        setShowConfirmationModal(false);
        return;
      }

      if (data?.success && data.twoFARequired === false) {
        setShowConfirmationModal(false);
        await fetchLinkedInStatusSafe();
        setCurrentStep(3);
        return;
      }

      throw new Error(data?.error || "Login failed");
    } catch (e) {
      console.error("start-login failed", e);
      setError(tt("errors.saveSettings", "Failed to save settings."));
    } finally {
      setSavingCookie(false);
    }
  };

  const submit2FA = async () => {
    if (!sessionId || pin.trim().length !== 6 || savingCookie) return;
    setSavingCookie(true);
    setError("");

    try {
      const { data } = await api.post("/linkedin/complete-2fa", {
        sessionId,
        twoFACode: pin.trim(),
      });

      if (data?.success) {
        setShow2FAModal(false);
        setSessionId(null);
        setPin("");
        await fetchLinkedInStatusSafe();
        setCurrentStep(3);
        return;
      }

      throw new Error(data?.error || "2FA failed");
    } catch (e) {
      console.error("complete-2fa failed", e);
      setError(tt("errors.saveSettings", "Failed to save settings."));
    } finally {
      setSavingCookie(false);
    }
  };

  // -----------------------------
  // Step 3: Email connect (optional)
  // -----------------------------
  const handleConnectGoogle = async () => {
    setError("");
    setOauthStarting(true);
    try {
      const res = await api.get("/email/oauth/google/start");
      const url = res.data?.url;
      if (url) window.location.href = url;
      else setError("Could not start Google connection (missing url).");
    } catch (e) {
      setError(safeErrorText(e));
    } finally {
      setOauthStarting(false);
    }
  };

  const handleConnectMicrosoft = async () => {
    setError("");
    setOauthStarting(true);
    try {
      const res = await api.get("/email/oauth/microsoft/start");
      const url = res.data?.url;
      if (url) window.location.href = url;
      else setError("Could not start Microsoft connection (missing url).");
    } catch (e) {
      setError(safeErrorText(e));
    } finally {
      setOauthStarting(false);
    }
  };

  const completeOnboarding = async ({ emailSkipped = false } = {}) => {
    try {
      await api.put("/user/onboarding", { emailSkipped, completed: true });
    } catch (e) {
      console.error("Failed to persist onboarding completion:", e);
    }
    setSkippedEmail(emailSkipped || skippedEmail);
    setCurrentStep(4);
  };

  const handleSkipEmail = async () => {
    setError("");
    await completeOnboarding({ emailSkipped: true });
  };

  const totalSteps = 4;

  const rightPanelConfig = useMemo(() => {
    if (currentStep === 1) {
      return {
        image: "/images/onboarding-location.png",
        title: "Set your location",
        bullets: [
          "Choose the country you will work from.",
          "This helps keep your LinkedIn sessions stable.",
          "If you use a proxy, match the country with that proxy.",
        ],
      };
    }
    if (currentStep === 2) {
      return {
        image: "/images/onboarding-linkedin.png",
        title: "Connect your LinkedIn account",
        bullets: [
          "We’ll save a LinkedIn cookie to automate your campaigns.",
          "If 2FA is enabled, you’ll be asked for a 6-digit code.",
          "If you already connected LinkedIn before, you can continue immediately.",
        ],
      };
    }
    if (currentStep === 3) {
      return {
        image: "/images/onboarding-email.png",
        title: "Connect your email (optional)",
        bullets: [
          "Recommended: send campaign emails from your mailbox.",
          "Choose Google (Gmail/Workspace) or Microsoft (Outlook/365).",
          "Skip if you want Leadgenflow to send emails for now.",
        ],
      };
    }
    return {
      image: "/images/onboarding-done.png",
      title: "You’re ready to go",
      bullets: ["Create a lead list", "Create your first campaign", "Start generating leads automatically"],
    };
  }, [currentStep]);

  const goStep = (n) => {
    if (n === 1) return setCurrentStep(1);
    if (n === 2) {
      if (!step1Done && !country) return;
      return setCurrentStep(2);
    }
    if (n === 3) {
      if (!step1Done) return;
      if (!step2Done) return;
      return setCurrentStep(3);
    }
    if (n === 4) {
      if (!step1Done) return;
      if (!step2Done) return;
      return setCurrentStep(4);
    }
  };

  const renderLeft = () => {
    if (currentStep === 1) {
      return (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {tt("onboarding.selectCountry", "Select country")}
          </h2>

          <p className="text-gray-600 mb-4">
            Choose the country you’ll operate from. This is used to keep your LinkedIn session stable.
          </p>

          {savedCountryExists && (
            <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-sm text-amber-900 font-medium">Location is already set.</p>
              <p className="text-sm text-amber-800 mt-1">
                To change your location, please contact{" "}
                <span className="font-semibold">support@leadgenflow.io</span>.
              </p>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          <label className="block text-sm font-medium text-gray-700 mb-2">
            {tt("settings.country", "Country")}
          </label>

          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 bg-blue-50 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            disabled={loadingUser || savedCountryExists}
          >
            <option value="">{tt("settings.selectCountry", "Select a country")}</option>
            {sortedCountries.map((c) => (
              <option key={c.alpha3} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          {!savedCountryExists && (
            <div className="mt-5 flex items-center gap-2">
              <button
                type="button"
                onClick={saveLocation}
                disabled={loadingUser || !country || savingLocation}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-md text-sm"
              >
                {savingLocation ? "Saving…" : "Save & continue →"}
              </button>
              <div className="text-xs text-gray-500">You can update this later (admin-only).</div>
            </div>
          )}

          {error && <p className="text-red-500 mt-4 text-sm">{error}</p>}
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {tt("settings.linkedInAccess", "LinkedIn access")}
              </h2>
              <p className="text-gray-600">{tt("settings.connectLinkedInInfo", "Connect your LinkedIn account.")}</p>
            </div>

            <button
              type="button"
              onClick={() => fetchLinkedInStatusSafe()}
              disabled={linkedinStatusLoading}
              className="bg-white hover:bg-gray-50 disabled:bg-gray-100 text-gray-800 px-4 py-2 rounded-md text-sm border"
            >
              {linkedinStatusLoading ? "Checking…" : "Refresh"}
            </button>
          </div>

          {linkedinConnected && (
            <div className="mt-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
              <p className="text-sm text-green-900 font-medium">LinkedIn is already connected ✅</p>
              <p className="text-sm text-green-800 mt-1">
                You can continue, or re-connect if you want to replace the cookie.
              </p>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setCurrentStep(3)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          <div className="mt-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{tt("settings.email", "Email")}</label>
              <input
                className="mt-1 block w-full rounded-md border border-gray-300 bg-blue-50 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                type="text"
                placeholder={tt("settings.email", "Email")}
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{tt("settings.password", "Password")}</label>
              <input
                className="mt-1 block w-full rounded-md border border-gray-300 bg-blue-50 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                type="password"
                placeholder={tt("settings.password", "Password")}
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Mode</label>
              <select
                value={selectedMode}
                onChange={(e) => setSelectedMode(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-white shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              >
                <option value="linkedin_no_2fa">{tt("settings.linkedinNo2FA", "LinkedIn login (no 2FA)")}</option>
                <option value="linkedin_2fa">{tt("settings.linkedinAndSalesNavigator", "LinkedIn + Sales Navigator (2FA)")}</option>
              </select>
            </div>

            <div className="text-xs text-gray-500">
              Logged in as <span className="font-medium">{loggedInEmail}</span>
            </div>

            <div className="pt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowConfirmationModal(true)}
                disabled={!emailInput || !passwordInput || savingCookie}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-md text-sm"
              >
                {savingCookie ? "Saving…" : "Save LinkedIn & continue →"}
              </button>

              {linkedinConnected && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(3)}
                  className="bg-white hover:bg-gray-50 text-gray-800 px-4 py-2 rounded-md text-sm border"
                >
                  Skip reconnect
                </button>
              )}
            </div>
          </div>

          {error && <p className="text-red-500 mt-4 text-sm">{error}</p>}
        </div>
      );
    }

    if (currentStep === 3) {
      const connected = !!oauthStatus?.connected;
      const providerLabel =
        oauthStatus?.provider === "microsoft"
          ? "Microsoft"
          : oauthStatus?.provider === "google"
          ? "Google"
          : "";

      return (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Connect email <span className="text-sm font-medium text-gray-500">(optional)</span>
              </h2>
              <p className="text-gray-600">
                Recommended: send campaign emails from your own mailbox. If you skip, Leadgenflow will send emails for now.
                Admins can change this later in Settings.
              </p>
            </div>

            <button
              type="button"
              onClick={fetchOAuthStatusSafe}
              disabled={oauthLoading}
              className="bg-white hover:bg-gray-50 disabled:bg-gray-100 text-gray-800 px-4 py-2 rounded-md text-sm border"
            >
              {oauthLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          <div className="mt-5 rounded-xl border bg-gray-50 p-4">
            {!connected ? (
              <>
                <div className="text-sm font-semibold text-gray-800">Not connected yet</div>
                <div className="text-xs text-gray-600 mt-1">Choose a provider or skip for now.</div>

                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={handleConnectGoogle}
                    disabled={oauthStarting}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-md text-sm"
                  >
                    {oauthStarting ? "Starting…" : "Connect Google"}
                  </button>

                  <button
                    type="button"
                    onClick={handleConnectMicrosoft}
                    disabled={oauthStarting}
                    className="bg-gray-900 hover:bg-black disabled:bg-gray-300 text-white px-4 py-2 rounded-md text-sm"
                  >
                    {oauthStarting ? "Starting…" : "Connect Microsoft"}
                  </button>
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSkipEmail}
                    className="bg-white hover:bg-gray-50 text-gray-800 px-4 py-2 rounded-md text-sm border"
                  >
                    Skip for now
                  </button>
                  <div className="text-xs text-gray-500">You can connect later in Settings (admins only).</div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">Connected</div>
                    <div className="text-sm text-gray-900 mt-1 break-all">
                      {safeText(oauthStatus.email)}{" "}
                      <span className="text-gray-500">({providerLabel})</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Workflow emails will prefer this mailbox for sending.</div>
                  </div>

                  <div className="inline-flex items-center gap-2 text-green-700 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full text-xs font-medium">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    Connected
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => completeOnboarding({ emailSkipped: false })}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
                  >
                    Continue →
                  </button>
                </div>
              </>
            )}
          </div>

          {error && <p className="text-red-500 mt-4 text-sm">{error}</p>}
        </div>
      );
    }

    // ✅ Step 4: FIXED UI (no translation keys showing)
    return (
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {tt("onboarding.completedTitle", "You’re ready to go")}
            </h2>
            <p className="text-gray-600">
              {tt(
                "onboarding.completedMessage",
                "Your setup is complete. Next, create a lead list and start your first campaign."
              )}
            </p>
          </div>

          <div className="hidden sm:flex flex-col items-end gap-2">
            {oauthStatus?.connected ? (
              <span className="inline-flex items-center gap-2 text-green-700 bg-green-50 border border-green-100 px-3 py-1 rounded-full text-xs font-medium">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                Email connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-600 bg-gray-50 border border-gray-200 px-3 py-1 rounded-full text-xs font-medium">
                <span className="w-2 h-2 rounded-full bg-gray-400" />
                Email not connected
              </span>
            )}
            {linkedinConnected ? (
              <span className="inline-flex items-center gap-2 text-green-700 bg-green-50 border border-green-100 px-3 py-1 rounded-full text-xs font-medium">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                LinkedIn connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-600 bg-gray-50 border border-gray-200 px-3 py-1 rounded-full text-xs font-medium">
                <span className="w-2 h-2 rounded-full bg-gray-400" />
                LinkedIn not connected
              </span>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="text-sm font-semibold text-gray-900 mb-2">
              Next steps
            </div>
            <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-1">
              <li>{tt("onboarding.createLeadList", "Create a lead list")}</li>
              <li>{tt("onboarding.createCampaign", "Create your first campaign")}</li>
            </ol>

            <div className="mt-4 text-xs text-gray-500">
              If you skipped email, campaign emails will be sent via Leadgenflow until an admin connects a mailbox/domain.
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="text-sm font-semibold text-gray-900 mb-2">
              Quick actions
            </div>

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => navigate("/leads")}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
              >
                {tt("onboarding.goToLeads", "Go to Leads")}
              </button>

              <button
                type="button"
                onClick={() => navigate("/campaigns")}
                className="w-full bg-white hover:bg-gray-50 text-gray-900 px-4 py-2 rounded-md text-sm border border-gray-200"
              >
                {tt("onboarding.goToCampaigns", "Go to Campaigns")}
              </button>
            </div>

            <div className="mt-4 text-xs text-gray-500">
              You can always update advanced settings later (admin-only).
            </div>
          </div>
        </div>
      </div>
    );
  };

  const bottomLeft = collapsed ? "4rem" : "16rem";
  const bottomWidth = collapsed ? "calc(100% - 4rem)" : "calc(100% - 16rem)";

  const isBusy =
    loadingUser || savingLocation || savingCookie || oauthStarting || oauthLoading || linkedinStatusLoading;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <div className="flex-grow w-full px-4 py-6">
        <div className="max-w-[1200px] mx-auto">
          <h1 className="text-2xl font-semibold text-gray-900 mb-3">
            {tt("onboarding.title", "Onboarding")}
          </h1>

          <div className="flex flex-wrap gap-2 mb-6">
            <StepPill
              index={1}
              label={tt("onboarding.selectCountry", "Select country")}
              active={currentStep === 1}
              done={step1Done}
              onClick={() => goStep(1)}
            />
            <StepPill
              index={2}
              label={tt("settings.linkedInAccess", "LinkedIn access")}
              active={currentStep === 2}
              done={step2Done}
              onClick={() => goStep(2)}
            />
            <StepPill
              index={3}
              label={tt("onboarding.connectEmail", "Connect email")}
              active={currentStep === 3}
              done={step3Done}
              onClick={() => goStep(3)}
            />
            <StepPill
              index={4}
              label={tt("onboarding.completedTitle", "Done")}
              active={currentStep === 4}
              done={step4Done}
              onClick={() => goStep(4)}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            {renderLeft()}
            <InfoPanel imageSrc={rightPanelConfig.image} title={rightPanelConfig.title} bullets={rightPanelConfig.bullets} />
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      {currentStep < 4 && (
        <div
          className="fixed bottom-0 bg-white border-t border-gray-200 shadow z-50 transition-all duration-300"
          style={{ left: bottomLeft, width: bottomWidth }}
        >
          <div className="flex items-center justify-between w-full px-6 py-3 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-[200px]">
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="h-2 bg-blue-600 rounded-full transition-all"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
              <span className="text-sm text-gray-500 whitespace-nowrap">
                {tt("campaign.step", "Step")} {currentStep} / {totalSteps}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {currentStep > 1 && (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="text-sm text-gray-600 hover:text-gray-800"
                  disabled={isBusy}
                >
                  ← {tt("campaign.previous", "Previous")}
                </button>
              )}

              {currentStep === 1 && (
                <button
                  onClick={() => (step1Done ? setCurrentStep(2) : saveLocation())}
                  disabled={isBusy || (!step1Done && !country)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingLocation ? "Saving…" : "Next →"}
                </button>
              )}

              {currentStep === 2 && (
                <button
                  onClick={() => (step2Done ? setCurrentStep(3) : setShowConfirmationModal(true))}
                  disabled={isBusy || (!step2Done && (!emailInput || !passwordInput))}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingCookie ? "Saving…" : "Next →"}
                </button>
              )}

              {currentStep === 3 && (
                <button
                  onClick={() => setCurrentStep(4)}
                  disabled={isBusy}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Finish →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{tt("onboarding.confirmDetailsTitle", "Confirm details")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {tt("onboarding.confirmLocation", "Location:")} <strong>{country}</strong>
          </p>
          <p>
            {tt("onboarding.confirmEmail", "LinkedIn email:")} <strong>{emailInput}</strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmationModal(false)} disabled={savingCookie}>
            {tt("actions.cancel", "Cancel")}
          </Button>
          <Button variant="primary" onClick={beginLinkedInLogin} disabled={savingCookie}>
            {savingCookie ? <span className="spinner-border spinner-border-sm" role="status" /> : tt("actions.confirm", "Confirm")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 2FA modal */}
      <Modal show={show2FAModal} onHide={() => setShow2FAModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{tt("settings.twoFactorAuth", "Two-factor authentication")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{tt("settings.enterPinPrompt", "Enter the 6-digit code from your authenticator app.")}</p>
          <input
            className="form-control mb-3"
            type="text"
            placeholder="123456"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          />
          <button
            className="btn btn-primary w-100"
            onClick={submit2FA}
            disabled={savingCookie || pin.trim().length !== 6}
          >
            {savingCookie ? <span className="spinner-border spinner-border-sm" role="status" /> : tt("actions.submit", "Submit")}
          </button>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Onboarding;